import { api } from './client';

/*  Specchio dei contratti di TeraWorks.Application.Commesse.

    Tutto in sola lettura: la scheda di commessa mostra il consuntivo e da dove viene, e
    non modifica niente. Le scritture — budget, stato, date — arriveranno quando ci sarà
    un servizio di dominio che le governa.  */

export interface ConsuntivoCommessa {
  id: string;
  numero: string | null;
  codiceSede: string;
  stato: string;
  codiceClienteMago: string;
  cliente: string | null;
  descrizione: string;
  numeroOrdineCliente: string | null;
  inizioLavori: string | null;
  finePrevista: string | null;
  giorniAllaFine: number | null;
  oltreLaFinePrevista: boolean;
  prezzoConcordato: number;
  budgetMateriali: number;
  /** In EURO: e' il budget della manodopera in denaro, non un monte ore. */
  budgetOre: number;
  budgetTrasferte: number;
  budgetCosti: number;
  materialiDaOrdinare: number;
  /** Quello che si pagherà: arrivato più in arrivo. Il residuo chiuso ne sta fuori. */
  materialiImpegnati: number;
  materialiRicevuti: number;
  /** La parte dell'impegnato che deve ancora entrare in magazzino. */
  materialiInArrivo: number;
  /** Residuo chiuso: ordinato a cui si è rinunciato, fuori dai costi previsti. */
  materialiChiusi: number;
  materialiPrevisti: number;
  materialiSulBudget: number | null;
  oreLavorate: number;
  oreSulBudget: number | null;
  costoOreStimato: number;
  /** Dai rapportini approvati, come le ore. Zero vuol dire che nessuno ne ha dichiarate. */
  trasferte: number;
  trasferteSulBudget: number | null;
  costiPrevisti: number;
  marginePrevisto: number;
  marginePercentuale: number | null;
  ordini: number;
  richieste: number;
  rapportini: number;
  /** Dove si lavora: non è l'indirizzo del cliente, che è dove si manda la fattura. */
  indirizzoCantiere: string | null;
  /** Chilometri andata e ritorno. Null è «non stabilito», che non è zero. */
  kmTrasferta: number | null;
  /*  Da quale offerta è nata. Null su una commessa aperta a mano — un lavoro piccolo
      concordato al telefono — che è un caso normale, non un dato mancante.  */
  preventivoOrigineId: string | null;
  numeroPreventivo: string | null;
  /** Chi segue la commessa. Nulli se non c'è nessuno. */
  responsabileId: string | null;
  responsabile: string | null;
  /** Quando e da chi è stata segnata da fatturare. Nulli se non lo è. */
  daFatturareIl: string | null;
  daFatturareDa: string | null;
  ordiniInRitardo: number;
  righeRespinte: number;
  /** Richieste aperte e mai inviate da tre giorni o più. */
  richiesteFerme: number;
  materialiDaCodificare: number;
}

export interface OrdineDiCommessa {
  id: string;
  numero: string | null;
  stato: string;
  codiceFornitoreMago: string;
  fornitore: string | null;
  dataOrdine: string;
  consegnaAttesa: string | null;
  consegna: string;
  inRitardo: boolean;
  righe: number;
  impegnato: number;
  ricevuto: number;
  /** Residuo chiuso: merce che non arrivera' piu'. Spiega il divario fra i due sopra. */
  chiuso: number;
}

export interface RigaDaComprare {
  id: string;
  richiestaAcquistoId: string;
  numeroRichiesta: string | null;
  dataFabbisogno: string;
  inRitardo: boolean;
  descrizione: string;
  unitaMisura: string;
  daOrdinare: number;
  costoStimato: number | null;
  migliorCostoOggi: number | null;
  migliorFornitoreOggi: string | null;
  valore: number;
}

export interface OreDiCommessa {
  operaio: string | null;
  ore: number;
  costo: number;
  rapportini: number;
  dal: string | null;
  al: string | null;
}

export interface DettaglioCommessa {
  consuntivo: ConsuntivoCommessa;
  ordini: OrdineDiCommessa[];
  daComprare: RigaDaComprare[];
  ore: OreDiCommessa[];
}

/*  Le commesse su cui si puo' rapportare. Serve al rapportino sul telefono, dove non
    esiste una griglia da cui scegliere.  */
export interface CommessaAperta {
  id: string;
  numero: string | null;
  descrizione: string;
  cliente: string | null;
  /*  I chilometri da proporre sul rapportino. Null quando nessuno li ha stabiliti: il
      telefono allora non propone niente, invece di proporre zero — che vorrebbe dire
      «cantiere sotto casa» e sarebbe una risposta inventata.  */
  kmTrasferta: number | null;
}

export interface DatiCantiere {
  indirizzoCantiere: string | null;
  kmTrasferta: number | null;
}

/*  Quello che Google propone. Ci sono dentro anche i due indirizzi perché un numero di
    chilometri da solo non si può giudicare, mentre «da qui a lì» sì. `problema`
    valorizzato vuol dire che il calcolo non si è potuto fare — manca la chiave, manca
    l'indirizzo, Google non risponde — e non è un guasto: i km si scrivono a mano.  */
export interface CalcoloTrasferta {
  kmAndataRitorno: number | null;
  sede: string;
  indirizzoPartenza: string;
  indirizzoCantiere: string | null;
  problema: string | null;
}

export interface ConfigurazioneMappe {
  chiaveBrowser: string | null;
}

export interface PersonaDaScegliere {
  id: string;
  nome: string;
}

export interface ResponsabileCommessa {
  responsabileId: string | null;
}

export const commesse = {
  leggi: (id: string) => api.get<DettaglioCommessa>(`/commesse/${id}`),

  aperte: () => api.get<CommessaAperta[]>('/commesse/aperte'),

  /** Chiede a Google i chilometri: propone, non salva. */
  /*  L'indirizzo si manda perché i chilometri si chiedono mentre è ancora nel campo:
      senza, il server calcolerebbe su quello salvato.  */
  calcolaKm: (id: string, indirizzoCantiere?: string | null) =>
    api.post<CalcoloTrasferta>(`/commesse/${id}/cantiere/calcolo`, {
      indirizzoCantiere: indirizzoCantiere ?? null,
    }),

  /** Le utenze attive fra cui scegliere chi segue una commessa. */
  responsabili: () => api.get<PersonaDaScegliere[]>('/commesse/responsabili'),

  impostaResponsabile: (id: string, responsabileId: string | null) =>
    api.put<void>(`/commesse/${id}/responsabile`, { responsabileId }),

  segnaDaFatturare: (id: string) => api.post<void>(`/commesse/${id}/da-fatturare`, {}),

  togliDaFatturare: (id: string) => api.post<void>(`/commesse/${id}/da-fatturare/togli`, {}),

  impostaCantiere: (id: string, dati: DatiCantiere) =>
    api.put<DatiCantiere>(`/commesse/${id}/cantiere`, dati),

  /** La chiave pubblica del quadratino di mappa: cambia da installazione a installazione. */
  configurazioneMappe: () => api.get<ConfigurazioneMappe>('/configurazione/mappe'),
};
